if (!customElements.get('mas-ksp-info')) {
  customElements.define('mas-ksp-info', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      if (!this.querySelector('template[data-mas-ksp-last]')) {
        if (!this.readyObserver) {
          this.readyObserver = new MutationObserver(() => {
            if (this.querySelector('template[data-mas-ksp-last]')) {
              this.readyObserver.disconnect(); this.readyObserver = null; this.connectedCallback();
            }
          });
          this.readyObserver.observe(this, { childList: true, subtree: true });
        }
        return;
      }
      this.root = this.closest('.mas-ksp, .mas-cart-reassurance');
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('.mas-ksp-dialog__content');
      this.headingElement = this.querySelector('.mas-ksp-dialog__header h2');
      if (!this.root || !this.dialog || !this.content) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.root.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-mas-ksp-open]');
        if (!trigger || !this.root.contains(trigger)) return;
        event.preventDefault();
        const panel = trigger.dataset.masKspOpen;
        // The app can expose a queue before its widget loads (or when blocked).
        // Keep the contact fallback available until the launcher is actually ready.
        const chatLauncher = panel === 'contact' && document.querySelector('img[alt="Launcher icon"][src*="chative.io"]');
        if (chatLauncher && chatLauncher.getBoundingClientRect().width > 0 && typeof window.ChativeApi === 'function') {
          try { window.ChativeApi('openChatWindow'); return; } catch (error) {}
        }
        this.openPanel(panel, trigger);
      }, options);
      this.querySelector('[data-mas-ksp-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
      }, options);
      // Keep Escape inside the topmost policy dialog when it sits above a cart drawer.
      window.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.dialog.open) {
          event.preventDefault(); event.stopImmediatePropagation(); this.dialog.close();
        }
      }, { ...options, capture: true });
      this.dialog.addEventListener('close', () => this.finishClose(), options);
      this.content.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-mas-ksp-anchor]');
        if (!link) return;
        const target = this.content.querySelector('#' + CSS.escape(link.dataset.masKspAnchor));
        if (target) {
          event.preventDefault();
          this.content.scrollTo({ top: target.getBoundingClientRect().top - this.content.getBoundingClientRect().top + this.content.scrollTop - 12, behavior: 'auto' });
        }
      }, options);
    }
    openPanel(name, trigger) {
      const source = Array.from(this.querySelectorAll('template[data-mas-ksp-panel]')).find((template) => template.dataset.masKspPanel === name);
      if (!source) return;
      // A dialog close event is queued; finish the previous session before a rapid reopen.
      if (!this.dialog.open && this.returnFocus) this.finishClose();
      const fragment = source.content.cloneNode(true);
      // Keep editorial content, without page-level scripts/styles or forms changing the purchase flow.
      fragment.querySelectorAll('script,style,link,base,meta,form,input,select,textarea,button,object,embed').forEach((node) => node.remove());
      const ids = new Map();
      fragment.querySelectorAll('*').forEach((node) => {
        Array.from(node.attributes).forEach((attribute) => {
          if (/^on/i.test(attribute.name) || attribute.name === 'style' || attribute.name === 'class') node.removeAttribute(attribute.name);
        });
        if (node.id) {
          const original = node.id;
          node.id = this.dialog.id + '-' + name + '-' + original;
          ids.set(original, node.id);
        }
      });
      fragment.querySelectorAll('a[href]').forEach((link) => {
        const href = link.getAttribute('href');
        if (href.startsWith('#') && ids.has(href.slice(1))) {
          const destination = ids.get(href.slice(1));
          link.href = '#' + destination;
          link.dataset.masKspAnchor = destination;
          return;
        }
        let url;
        try { url = new URL(href, location.origin); } catch (error) { link.removeAttribute('href'); return; }
        if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) { link.removeAttribute('href'); return; }
        if (url.protocol === 'mailto:' || url.protocol === 'tel:') {
          link.removeAttribute('target');
          link.removeAttribute('rel');
          return;
        }
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.title = 'Opens in a new tab';
        link.setAttribute('aria-label', (link.textContent || 'Link').trim() + ' (opens in a new tab)');
      });
      fragment.querySelectorAll('img').forEach((image) => { image.loading = image.hasAttribute('data-care-primary') ? 'eager' : 'lazy'; image.decoding = 'async'; });
      fragment.querySelectorAll('iframe').forEach((frame) => {
        const src = frame.getAttribute('src') || '';
        if (!/^(https?:)?\/\//i.test(src) || frame.hasAttribute('srcdoc')) { frame.remove(); return; }
        frame.loading = 'lazy';
        frame.removeAttribute('width');
        frame.removeAttribute('height');
        if (!frame.title) frame.title = source.dataset.title;
      });
      this.openContent(fragment, source.dataset.title, trigger);
    }
    // Interactive theme components supply their own DOM; policy templates stay sanitized above.
    openContent(fragment, title, trigger) {
      if (!this.dialog.open && this.returnFocus) this.finishClose();
      this.returnFocus = trigger;
      this.headingElement.textContent = title;
      this.content.replaceChildren(fragment);
      this.updateViewport();
      if (!this.dialog.open) {
        this.scrollStyles = [document.documentElement, document.body].map((element) => ({
          element, value: element.style.getPropertyValue('overflow'), priority: element.style.getPropertyPriority('overflow')
        }));
        this.scrollStyles.forEach(({ element }) => element.style.setProperty('overflow', 'hidden'));
        this.viewportEvents = new AbortController();
        const viewport = window.visualViewport || window;
        const viewportOptions = { signal: this.viewportEvents.signal, passive: true };
        viewport.addEventListener('resize', () => this.updateViewport(), viewportOptions);
        viewport.addEventListener('scroll', () => this.updateViewport(), viewportOptions);
        this.dialog.showModal();
      }
      this.content.scrollTop = 0;
      this.querySelector('[data-mas-ksp-close]').focus({ preventScroll: true });
      this.updateViewport();
    }
    updateViewport() {
      // Mobile Chromium can pan a visual viewport inside a wider layout viewport.
      // Keep the dialog in the visible area, including after focus or browser UI changes.
      const viewport = window.visualViewport;
      const values = {
        width: viewport ? viewport.width : document.documentElement.clientWidth,
        height: viewport ? viewport.height : document.documentElement.clientHeight,
        x: viewport ? viewport.offsetLeft : 0,
        y: viewport ? viewport.offsetTop : 0
      };
      Object.entries(values).forEach(([name, value]) => {
        this.dialog.style.setProperty('--mas-dialog-' + name, value + 'px');
      });
    }
    releaseScroll() {
      if (this.viewportEvents) this.viewportEvents.abort();
      this.viewportEvents = null;
      (this.scrollStyles || []).forEach(({ element, value, priority }) => {
        if (value) element.style.setProperty('overflow', value, priority);
        else element.style.removeProperty('overflow');
      });
      this.scrollStyles = null;
    }
    finishClose() {
      // A delayed close event must not clear a newly opened panel.
      if (this.dialog && this.dialog.open) return;
      this.releaseScroll();
      this.content.replaceChildren();
      if (this.returnFocus && this.returnFocus.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }
    disconnectedCallback() {
      if (this.dialog && this.dialog.open) this.dialog.close();
      this.releaseScroll();
      if (this.events) this.events.abort();
      if (this.readyObserver) this.readyObserver.disconnect();
      this.readyObserver = null;
      this.events = null;
    }
  });
}
